<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { IScope } from '@/types/filterBuilderTypes';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import ScheduleFormFields from './ScheduleFormFields.vue';
import ScheduleTypeIcon from './ScheduleTypeIcon.vue';
import { modelIcon } from '@/composables/modelIcons';
import PlaylistContainerZoneLine from '@/components/DataTable/PlaylistContainerZoneLine.vue';
import { zoneLineRowFor } from './zoneLineRow';
import ModelSearchPicker from '@/components/ModelSearchPicker.vue';
import ZoneSearchOption from '@/components/ZoneSearchOption.vue';
import { zoneIconFor } from '@/composables/useZoneIcon';
import { zoneSummaryFor } from '@/composables/api/useZoneSummaries';
import { playerNameFor } from '@/composables/api/usePlayerSummaries';
import {
    scheduleWriteErrorMessage,
    useCreateSchedule,
    useUpdateSchedule,
} from '@/composables/api/useScheduleMutations';
import {
    EDITABLE_SCHEDULE_KINDS,
    SCHEDULE_FORM_MESSAGES,
    SCHEDULING_MODE,
    emptyScheduleForm,
    isEditableKind,
    rowToScheduleForm,
    scheduleFormToPayload,
    validateScheduleForm,
    type EditableScheduleKind,
    type ScheduleForm,
    type ScheduleRow,
} from '@/utils/customPlaylistSchedule';

/**
 * Create or edit one custom playlist schedule (decisions D4, D5; amendments
 * A1, A2, A7). The playlist is fixed to the list the dialog was opened from;
 * Silence shows the word "Silence" instead, because the backend puts a new
 * silence row on the zone's own "Silence Schedule" list.
 *
 * Save validates, then asks for confirmation (tabasco-react's "Save Changes"
 * step), then writes. The dialog stays open with the server's message when the
 * write fails. Station change rows, and anyone without the update permission,
 * get the same fields read-only.
 */
const props = withDefaults(
    defineProps<{
        /** The saved schedule, or null to add one. */
        schedule: ScheduleRow | null;
        listId: number;
        listName?: string | null;
        /** The list's own zone; a new schedule belongs to it. */
        ownerZoneId: number;
        ownerZoneName?: string | null;
        /** Show the fields but no Save (no update/create permission). */
        readOnly?: boolean;
    }>(),
    { listName: null, ownerZoneName: null, readOnly: false },
);

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
    (event: 'saved', row: Record<string, unknown>): void;
}>();

const createMutation = useCreateSchedule();
const updateMutation = useUpdateSchedule();

const form = ref<ScheduleForm>(emptyScheduleForm());
const step = ref<'form' | 'confirm'>('form');
const attempted = ref(false);
const formError = ref<string | null>(null);

/**
 * Bumped every time the dialog opens. A save captures it before the request;
 * a result that comes back after the dialog was reopened, possibly on another
 * schedule, only shows its toast. It must not close that dialog or put its
 * error in that form. The X and Escape still close the dialog mid-save.
 */
const openCount = ref(0);

/** The zone a new schedule goes on. It starts as the list's own zone. */
const chosenZoneId = ref<number>(props.ownerZoneId);
/** The opening whose save is in flight, so only that opening shows "Saving…". */
const savingFor = ref<number | null>(null);

watch(
    open,
    (isOpen) => {
        if (!isOpen) return;
        openCount.value += 1;
        form.value = props.schedule ? rowToScheduleForm(props.schedule) : emptyScheduleForm('oneoff');
        chosenZoneId.value = props.ownerZoneId;
        step.value = 'form';
        attempted.value = false;
        formError.value = null;
    },
    { immediate: true },
);

const isCreate = computed(() => props.schedule === null || props.schedule.id == null);
const kindIsEditable = computed(() => isEditableKind(form.value.kind));
const isReadOnly = computed(() => props.readOnly || !kindIsEditable.value);

/** The zone the schedule is on: the chosen zone for a new one, else the row's own zone. */
const scheduleZoneId = computed<number>(() => {
    const rowZone = Number(props.schedule?.location_zone_id);
    return !isCreate.value && Number.isInteger(rowZone) && rowZone > 0 ? rowZone : chosenZoneId.value;
});

const isOnAnotherZone = computed(() => scheduleZoneId.value !== props.ownerZoneId);

/**
 * A new schedule can go on the list's own zone or, when that zone is a master, on one of its
 * subscribers. The server holds the same line. A zone with no subscribers offers no choice.
 */
const canChooseZone = computed(
    () => open.value && isCreate.value && !isReadOnly.value && (zoneSummaryFor(props.ownerZoneId)?.subscribersCount ?? 0) > 0,
);

/** The zones a new schedule may go on: the list's own zone and the zones that subscribe to it. */
const zoneChoiceScope = computed<IScope>(() => ({
    id: 'schedule-zone-choices',
    scopeType: 'or',
    rules: [
        { id: 'schedule-zone-own', column: 'id', criterion: 'in', value: [props.ownerZoneId], field: null },
        { id: 'schedule-zone-subscribers', column: 'master_location_zone_id', criterion: 'in', value: [props.ownerZoneId], field: null },
    ],
}));

/** The chosen zone's name, for the Zone field's button. */
const chosenZoneName = computed(() =>
    chosenZoneId.value === props.ownerZoneId && props.ownerZoneName
        ? props.ownerZoneName
        : (zoneSummaryFor(chosenZoneId.value)?.displayString ?? `Zone #${chosenZoneId.value}`),
);

function onZoneChosen(value: string | null): void {
    const zoneId = Number(value);
    if (Number.isInteger(zoneId) && zoneId > 0) chosenZoneId.value = zoneId;
}

/**
 * Focus the first field when the dialog opens. The dialog's own default is its first tabbable
 * element, which was the location link in the zone line under the title.
 *
 * A read-only dialog has no field to focus: its fieldset is disabled, which disables every
 * control in it without giving them a `disabled` attribute. Focus then goes to Close, so it never
 * stays behind the dialog.
 */
const formFields = ref<HTMLFieldSetElement | null>(null);

/** The field the dialog focused when it opened. */
let openingFocus: HTMLElement | null = null;

function firstEditableField(): HTMLElement | null {
    const fields = formFields.value;
    // The fieldset is the read-only switch. Checked directly, because not every DOM applies a
    // disabled fieldset to :enabled.
    return fields && !fields.disabled
        ? fields.querySelector<HTMLElement>('input:enabled, button:enabled, select:enabled, textarea:enabled')
        : null;
}

function focusFirstField(event: Event): void {
    const close = formFields.value
        ?.closest('[data-testid="schedule-editor-dialog"]')
        ?.querySelector<HTMLElement>('[data-testid="schedule-editor-cancel"]');
    const target = firstEditableField() ?? close;
    openingFocus = target ?? null;
    if (!target) return;
    event.preventDefault();
    target.focus();
}

/**
 * The Zone field is shown only once the lookup of the list's zone says the zone has
 * subscribers. That lookup can answer after the dialog has opened. The dialog has then
 * already focused Schedule type, and the Zone field appears above it. Move focus up to
 * the Zone field when it appears, as long as focus is still where the dialog put it.
 * If the operator has already moved focus, leave it alone.
 */
watch(
    canChooseZone,
    (canChoose) => {
        if (!canChoose || step.value !== 'form') return;
        const zoneField = firstEditableField();
        if (!zoneField || !openingFocus || document.activeElement !== openingFocus || zoneField === openingFocus) return;
        openingFocus = zoneField;
        zoneField.focus();
    },
    { flush: 'post' },
);

const zoneLookup = computed(() => (open.value ? zoneSummaryFor(scheduleZoneId.value) : undefined));

/** The zone's scheduling mode, once the lookup has answered (amendment A1). */
const schedulingModeText = computed(() => {
    const lookup = zoneLookup.value;
    if (!lookup) return null;
    return lookup.schedulingModeId === SCHEDULING_MODE.PRIORITY
        ? 'This zone uses Priority scheduling.'
        : 'This zone uses Legacy scheduling.';
});

const title = computed(() => {
    if (isReadOnly.value) return 'Schedule';
    return isCreate.value ? 'Create Schedule' : 'Edit Schedule';
});

/** "Switches to Main Street-Bar." once the player lookup answers, else "Switches to player #2007." */
const stationChangeText = computed(() => {
    const id = form.value.stationChangeLocationZonePlayerId;
    if (form.value.kind !== 'stationchange' || !id || !open.value) return null;
    const name = playerNameFor(id);
    return name ? `Switches to ${name}.` : `Switches to player #${id}.`;
});

const listIcon = modelIcon('LocationZoneCustomPlaylist');

/** The list's own zone line. */
const ownerZoneRow = computed(() => (open.value ? zoneLineRowFor(props.ownerZoneId, props.ownerZoneName) : null));
/** The schedule's zone line, only when that zone is not the list's own. */
const otherZoneRow = computed(() => (open.value && isOnAnotherZone.value ? zoneLineRowFor(scheduleZoneId.value) : null));

const playlistText = computed(() => (form.value.kind === 'silence' ? 'Silence' : props.listName ?? `List #${props.listId}`));

/** Create offers every editable kind; an edit stays within its family, as tabasco-react. */
const kindOptions = computed<EditableScheduleKind[]>(() => {
    if (isCreate.value) return [...EDITABLE_SCHEDULE_KINDS];
    const saved = props.schedule ? rowToScheduleForm(props.schedule).kind : form.value.kind;
    if (saved === 'oneoff' || saved === 'recurring') return ['oneoff', 'recurring'];
    if (saved === 'insert' || saved === 'message') return ['insert', 'message'];
    return [];
});

/** A new silence is attached to the zone's "Silence Schedule" list by the backend (decision P9). */
const silenceNotice = computed(() => {
    if (form.value.kind !== 'silence' || !isCreate.value) return null;
    const list = props.listName ?? 'this list';
    return `Silence is saved on this zone's Silence Schedule list, so it will not appear under ${list}.`;
});

const errors = computed(() => (attempted.value && !isReadOnly.value ? validateScheduleForm(form.value) : []));

function errorFor(...fields: string[]): string | null {
    return errors.value.find((error) => fields.includes(error.field))?.message ?? null;
}

const isSaving = computed(() => savingFor.value !== null && savingFor.value === openCount.value);

function requestSave(): void {
    if (isReadOnly.value) return;
    attempted.value = true;
    formError.value = null;
    if (validateScheduleForm(form.value).length > 0) return;
    step.value = 'confirm';
}

async function confirmSave(): Promise<void> {
    formError.value = null;
    const payload = scheduleFormToPayload(form.value, {
        locationZoneId: scheduleZoneId.value,
        locationZoneCustomPlaylistId: props.listId,
    });
    const creating = isCreate.value || form.value.id === null;
    const thisOpening = openCount.value;
    const stillShowing = () => open.value && openCount.value === thisOpening;
    savingFor.value = thisOpening;
    try {
        const saved = creating
            ? await createMutation.mutateAsync({ listId: props.listId, payload })
            : await updateMutation.mutateAsync({ listId: props.listId, scheduleId: form.value.id!, payload });
        toast.success(creating ? 'Schedule added.' : 'Schedule saved.');
        if (!stillShowing()) return;
        emit('saved', saved);
        open.value = false;
    } catch (error) {
        const message = scheduleWriteErrorMessage(error, "Couldn't save the schedule.");
        toast.error("Couldn't save the schedule", { description: message });
        if (!stillShowing()) return;
        formError.value = message;
        step.value = 'form';
    } finally {
        if (savingFor.value === thisOpening) savingFor.value = null;
    }
}

const fieldClass = 'flex flex-col gap-1.5';
const labelClass = 'text-sm font-medium';
const hintClass = 'text-muted-foreground text-xs';
</script>

<template>
    <Dialog v-model:open="open">
        <DialogContent class="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="schedule-editor-dialog" @open-auto-focus="focusFirstField">
            <template v-if="step === 'confirm'">
                <DialogHeader>
                    <DialogTitle>Save Changes</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to save these changes to the schedule? This action will update the current
                        programming immediately.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" :disabled="isSaving" data-testid="schedule-confirm-back" @click="step = 'form'">Back</Button>
                    <Button :disabled="isSaving" data-testid="schedule-confirm-save" @click="confirmSave">
                        {{ isSaving ? 'Saving…' : 'Confirm & Save' }}
                    </Button>
                </DialogFooter>
            </template>

            <form v-else class="flex flex-col gap-4" novalidate data-testid="schedule-editor-form" @submit.prevent="requestSave">
                <DialogHeader>
                    <DialogTitle>{{ title }}</DialogTitle>
                    <!-- The list, then its zone line, as the custom list card draws them. A
                         schedule on another zone adds that zone's line in a rail, as the card
                         does. Zones are always their linked zone line, never a sentence. -->
                    <DialogDescription as="div" class="flex min-w-0 flex-col gap-1" data-testid="schedule-editor-context">
                        <span class="text-foreground inline-flex min-w-0 items-center gap-1.5 font-medium">
                            <ScheduleTypeIcon v-if="form.kind === 'silence'" kind="silence" decorative />
                            <component :is="listIcon" v-else-if="listIcon" class="size-4 shrink-0" aria-hidden="true" data-testid="schedule-editor-list-icon" />
                            {{ playlistText }}
                        </span>
                        <PlaylistContainerZoneLine v-if="ownerZoneRow" :row="ownerZoneRow" class="min-w-0 text-sm" data-testid="schedule-editor-zone" />
                        <div v-if="otherZoneRow" class="border-border border-l-2 pl-2" data-testid="schedule-editor-other-zone">
                            <PlaylistContainerZoneLine :row="otherZoneRow" class="min-w-0 text-sm" />
                        </div>
                    </DialogDescription>
                    <p v-if="silenceNotice" class="text-sm" data-testid="schedule-editor-silence-notice">{{ silenceNotice }}</p>
                    <p v-if="!kindIsEditable" :class="hintClass" data-testid="schedule-editor-read-only-kind">
                        {{ SCHEDULE_FORM_MESSAGES.READ_ONLY_KIND }}
                    </p>
                </DialogHeader>

                <fieldset ref="formFields" :disabled="isReadOnly" class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <ScheduleFormFields
                        v-model:form="form"
                        mode="single"
                        :kind-options="kindOptions"
                        :kind-is-editable="kindIsEditable"
                        :read-only="isReadOnly"
                        :error-for="errorFor"
                        :scheduling-mode-text="schedulingModeText"
                        :station-change-text="stationChangeText"
                    >
                        <template #before>
                    <!-- Where a new schedule plays: the list's zone, or a zone that subscribes to it. -->
                    <div v-if="canChooseZone" :class="[fieldClass, 'sm:col-span-2']" data-testid="schedule-zone-field">
                        <span :class="labelClass">Zone</span>
                        <ModelSearchPicker
                            :model-value="String(chosenZoneId)"
                            slug="location_zone"
                            :scope="zoneChoiceScope"
                            search-placeholder="Search this zone and its subscribers…"
                            test-id-prefix="schedule-zone-picker"
                            @update:model-value="onZoneChosen"
                        >
                            <template #selected>
                                <span class="flex min-w-0 items-center gap-1.5">
                                    <component :is="zoneIconFor({ id: chosenZoneId })" class="size-3.5 shrink-0" aria-hidden="true" />
                                    <span class="truncate">{{ chosenZoneName }}</span>
                                </span>
                            </template>
                            <template #option="{ row, label }">
                                <ZoneSearchOption :row="row" :label="label" />
                            </template>
                        </ModelSearchPicker>
                    </div>
                        </template>
                    </ScheduleFormFields>
                </fieldset>

                <p v-if="formError" class="text-destructive text-sm" role="alert" data-testid="schedule-editor-error">{{ formError }}</p>

                <DialogFooter>
                    <Button type="button" variant="outline" data-testid="schedule-editor-cancel" @click="open = false">
                        {{ isReadOnly ? 'Close' : 'Cancel' }}
                    </Button>
                    <Button v-if="!isReadOnly" type="submit" data-testid="schedule-editor-save">Save</Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
